/**
 * @file コンポーネント：フル動画コンテンツ DOM管理
 */

// ライブラリ読み込み
import React, { Suspense } from "react";

// コンポーネント読み込み
const Buttonlist = React.lazy(() => import("~/src/components/pages/Video/Video/Buttonlist").then((module) => ({ default: module.Buttonlist, })));
const ModalTermsVideo = React.lazy(() => import("~/src/components/pages/Video/Video/ModalTermsVideo").then((module) => ({ default: module.ModalTermsVideo, })));
const PopupNote = React.lazy(() => import("~/src/components/pages/Video/Video/PopupNote").then((module) => ({ default: module.PopupNote, })));
const PopupPayment = React.lazy(() => import("~/src/components/pages/Video/Video/PopupPayment").then((module) => ({ default: module.PopupPayment, })));
const PopupThread = React.lazy(() => import("~/src/components/pages/Video/Video/PopupThread").then((module) => ({ default: module.PopupThread, })));
const RelatedCases = React.lazy(() => import("~/src/components/pages/Video/Video/RelatedCases").then((module) => ({ default: module.RelatedCases, })));
const DrawerNote = React.lazy(() => import("~/src/components/projects/DrawerNote").then((module) => ({ default: module.DrawerNote, })));
const Footer = React.lazy(() => import("~/src/components/projects/Footer").then((module) => ({ default: module.Footer, })));
const ModalPrivacypolicy = React.lazy(() => import("~/src/components/projects/ModalPrivacypolicy").then((module) => ({ default: module.ModalPrivacypolicy, })));
const ModalTerms = React.lazy(() => import("~/src/components/projects/ModalTerms").then((module) => ({ default: module.ModalTerms, })));
const ButtonAd = React.lazy(() => import("~/src/components/projects/Video/ButtonAd").then((module) => ({ default: module.ButtonAd, })));
const ButtonLink = React.lazy(() => import("~/src/components/projects/Video/ButtonLink").then((module) => ({ default: module.ButtonLink, })));
const ContentsAd = React.lazy(() => import("~/src/components/projects/Video/ContentsAd").then((module) => ({ default: module.ContentsAd, })));
const ContentsBasic = React.lazy(() => import("~/src/components/projects/Video/ContentsBasic").then((module) => ({ default: module.ContentsBasic, })));
const ContentsController = React.lazy(() => import("~/src/components/projects/Video/ContentsController").then((module) => ({ default: module.ContentsController, })));
import { ContentsMain } from "~/src/components/projects/Video/ContentsMain";
const Header = React.lazy(() => import("~/src/components/projects/Video/Header").then((module) => ({ default: module.Header, })));
const ModalEdit = React.lazy(() => import("~/src/components/projects/Video/ModalEdit").then((module) => ({ default: module.ModalEdit, })));
const ModalErrorLoading = React.lazy(() => import("~/src/components/projects/Video/ModalErrorLoading").then((module) => ({ default: module.ModalErrorLoading, })));
const ModalErrorSession = React.lazy(() => import("~/src/components/projects/Video/ModalErrorSession").then((module) => ({ default: module.ModalErrorSession, })));
const PopupSchema = React.lazy(() => import("~/src/components/projects/Video/PopupSchema").then((module) => ({ default: module.PopupSchema, })));
const Loading = React.lazy(() => import("~/src/components/uiparts/Loading").then((module) => ({ default: module.Loading, })));

// 関数読み込み
import { fnIsAndroid, fnIsIpad, fnIsIphone } from "~/src/functions/boolean";

// フック読み込み
import { mediaQuery, useMediaQuery } from "~/src/hooks/mediaQuery";

// スタイル読み込み
import {
    StyledContents,
    StyledContentsData,
    StyledContentsDataLoading,
} from "./style";

// Presenterを返す
export const Presenter = (props) => {
    // ローカライズデータ
    const localized = window.listLocalized;

    return (
        <>
            {/* ヘッダー */}
            <Suspense><Header
                isOpenModalLogin={props.isOpenModalLogin}
                setIsOpenModalLogin={props.setIsOpenModalLogin}
                adOperationSummary={props.adOperationSummary}
                hasDigest={props.hasDigest}
                hasPointcontents={props.hasPointcontents}
                hasPostoperation={props.hasPostoperation}
                hasPreoperation={props.hasPreoperation}
                isEditPage={props.isEditPage}
                isPaid={props.isPaid}
                isPurchased={props.isPurchased}
                isSponsorUser={props.isSponsorUser}
                listSurgicalEquipment={props.listSurgicalEquipment}
                refContentsMenu={props.refContentsMenu}
                selectedContentsData={props.selectedContentsData}
                setIsHighlightPopupPayment={props.setIsHighlightPopupPayment}
                setSelectedContentsData={props.setSelectedContentsData}
                summary={props.summary}
            /></Suspense>
            <StyledContents>
                {/* データコンテンツ */}
                <StyledContentsData
                    className={props.isLoadingContentsData && "is-loading"}
                >
                    {false && props.isLoadingContentsData && (
                        // todo:ContentsMainのFullvideoPlayerサムネイル表示の方が早いため、ここは非表示とする
                        // ローディング中
                        <StyledContentsDataLoading>
                            <Loading />
                        </StyledContentsDataLoading>
                    )}
                    {/* 症例データコンテンツメイン */}
                    <ContentsMain
                        setIsOpenModalLogin={props.setIsOpenModalLogin}
                        fnSetModalEdit={props.fnSetModalEdit}
                        isEditPage={props.isEditPage}
                        isPaid={props.isPaid}
                        isPurchased={props.isPurchased}
                        isCompletionMediaPlayerFullvideo={props.isCompletionMediaPlayerFullvideo}
                        selectedContentsData={props.selectedContentsData}
                        setIsHighlightPopupPayment={
                            props.setIsHighlightPopupPayment
                        }
                        setSelectedContentsData={props.setSelectedContentsData}
                        // 術前情報で使用
                        hasDigest={props.hasDigest}
                        hasPreoperation={props.hasPreoperation}
                        slidersPreoperation={props.slidersPreoperation}
                        textPreoperation={props.textPreoperation}
                        // フル動画で使用
                        fairplayCer={props.fairplayCer}
                        fairplayLaUrl={props.fairplayLaUrl}
                        fnJump={props.fnJump}
                        hasAudio={props.hasAudio}
                        isLoadingFullvideo={props.isLoadingFullvideo}
                        isPlay={props.isPlay}
                        isSponsorContent={props.isSponsorContent}
                        languageCodeOperation={props.languageCodeOperation}
                        listComment={props.listComment}
                        listEvent={props.listEvent}
                        listTopic={props.listTopic}
                        mainSurgeonId={props.mainSurgeonId}
                        movieUrl={props.movieUrl}
                        operationId={props.operationId}
                        playreadyLaUrl={props.playreadyLaUrl}
                        refFullvideoPlayerButtonlistSetting={
                            props.refFullvideoPlayerButtonlistSetting
                        }
                        refFullvideoPlayerIconlist={
                            props.refFullvideoPlayerIconlist
                        }
                        refFullvideoPlayerJumpButton={
                            props.refFullvideoPlayerJumpButton
                        }
                        setMediaPlayerFullvideo={props.setMediaPlayerFullvideo}
                        setIsLoadingFullvideo={props.setIsLoadingFullvideo}
                        setPlayEvent={props.setPlayEvent}
                        timeProgress={props.timeProgress}
                        timeProgressSecond={props.timeProgressSecond}
                        timeTotalSecond={props.timeTotalSecond}
                        videoPlayRatioType={props.videoPlayRatioType}
                        widevineLaUrl={props.widevineLaUrl}
                        // 術後情報で使用
                        hasPostoperation={props.hasPostoperation}
                        slidersPostoperation={props.slidersPostoperation}
                        textPostoperation={props.textPostoperation}
                        // ポイントコンテンツで使用
                        hasPointcontents={props.hasPointcontents}
                        // 概要で使用
                        adOperationLink={props.adOperationLink}
                        adOperationSummary={props.adOperationSummary}
                        listSurgicalEquipment={props.listSurgicalEquipment}
                        refSummary={props.refSummary}
                        refTitle={props.refTitle}
                        summary={props.summary}
                    />
                    {/* 症例ページのリンクボタン */}
                    {props.contentPageLinkButtonText !== null && (
                        <ButtonLink
                            contentPageLink={props.contentPageLink}
                            contentPageLinkAnotherTabFlag={props.contentPageLinkAnotherTabFlag}
                            contentPageLinkButtonText={props.contentPageLinkButtonText}
                        />
                    )}
                    {/* 機器広告ボタン */}
                    {props.isCompletionMediaPlayerFullvideo === true && props.adOperationButtonText !== null && (
                        <Suspense><ButtonAd
                            adOperationButtonText={props.adOperationButtonText}
                            adOperationId={props.adOperationId}
                            operationId={props.operationId}
                            setIsOpenPopupAd={props.setIsOpenPopupAd}
                        /></Suspense>
                    )}
                    {/* 症例基本データ */}
                    {props.isCompletionMediaPlayerFullvideo === true && (
                    <Suspense><ContentsBasic
                        date={props.date}
                        fnSetModalEdit={props.fnSetModalEdit}
                        isAdminUser={props.isAdminUser}
                        isEditPage={props.isEditPage}
                        isFavorite={props.isFavorite}
                        isPaid={props.isPaid}
                        isPurchased={props.isPurchased}
                        isRate={props.isRate}
                        isSponsorUser={props.isSponsorUser}
                        languageCodeOperation={props.languageCodeOperation}
                        listLanguage={props.listLanguage}
                        operationId={props.operationId}
                        rateCount={props.rateCount}
                        refHeaderTitle={props.refHeaderTitle}
                        refSummary={props.refSummary}
                        refTitle={props.refTitle}
                        setIsFavorite={props.setIsFavorite}
                        setIsRate={props.setIsRate}
                        setRateCount={props.setRateCount}
                        surgeonlist={props.surgeonlist}
                        surgeonUserList={props.surgeonUserList}
                        taglist={props.taglist}
                        title={props.title}
                        view={props.view}
                    /></Suspense>)}
                    {/* コントローラーコンテンツ */}
                    {props.isCompletionMediaPlayerFullvideo === true && useMediaQuery(mediaQuery.pcOrPcWide) === false && (
                        <Suspense>
                            <ContentsController
                                setIsOpenModalLogin={props.setIsOpenModalLogin}
                                fnJump={props.fnJump}
                                fnSetModalEdit={props.fnSetModalEdit}
                                isAdminUser={props.isAdminUser}
                                isEditPage={props.isEditPage}
                                isLoadingContentsController={
                                    props.isLoadingContentsController
                                }
                                isLoadingTimeline={props.isLoadingTimeline}
                                languageCodeOperation={props.languageCodeOperation}
                                isPaid={props.isPaid}
                                isPurchased={props.isPurchased}
                                listEvent={props.listEvent}
                                listTimeline={props.listTimeline}
                                mainSurgeonId={props.mainSurgeonId}
                                operationId={props.operationId}
                                setClickedSlideIndex={props.setClickedSlideIndex}
                                setIsHighlightPopupPayment={
                                    props.setIsHighlightPopupPayment
                                }
                                setIsOpenPopupSchema={props.setIsOpenPopupSchema}
                                setPopupSchemaSlider={props.setPopupSchemaSlider}
                                setSelectedContentsData={
                                    props.setSelectedContentsData
                                }
                                timeProgress={props.timeProgress}
                            />
                        </Suspense>
                    )}
                    {props.isCompletionMediaPlayerFullvideo === true && props.isSponsorUser === false &&
                        props.isEditPage !== true && (
                            // スポンサーユーザー、執刀医権限フル動画ページ時は非表示
                            <>
                                {props.isPaid === true && (
                                    // 有料コンテンツの場合
                                    // その他有料コンテンツ
                                    <Suspense><RelatedCases
                                        isOpen={false}
                                        headline={
                                            localized["Label.OtherPaidContent"]
                                        }
                                        operationlist={props.operationlistPaid}
                                    /></Suspense>
                                )}
                                {props.hasPointcontents === true &&
                                    props.isPaid === false ? (
                                    // ポイントコンテンツの場合
                                    // 関連症例：同じカテゴリー（ポイントコンテンツ）の症例
                                    <Suspense><RelatedCases
                                        sliderId="content"
                                        isOpen={true}
                                        headline={
                                            localized[
                                            "Label.RelatedContent"
                                            ]
                                        }
                                        operationlist={props.operationlistPointcontents}
                                    /></Suspense>
                                ) : (
                                    // ポイントコンテンツではない場合
                                    // 関連症例：関連コンテンツの症例
                                    <Suspense><RelatedCases
                                        sliderId="content"
                                        isOpen={true}
                                        headline={
                                            localized[
                                            "Label.RelatedContent"
                                            ]
                                        }
                                        operationlist={props.operationlistRelatedContent}
                                    /></Suspense>
                                )}
                                {/* 関連症例：同じカテゴリー（術式分類）の症例 */}
                                <Suspense><RelatedCases
                                    sliderId="category"
                                    isOpen={true}
                                    headline={
                                        localized[
                                        "Label.RelatedCasesCategory"
                                        ]
                                    }
                                    operationlist={props.operationlistCategory}
                                /></Suspense>
                                {/* 関連症例：同じ執刀医の症例 */}
                                <Suspense><RelatedCases
                                    sliderId="surgeon"
                                    isOpen={true}
                                    headline={
                                        localized[
                                        "Label.RelatedCasesSurgeon"
                                        ]
                                    }
                                    operationlist={props.operationlistSurgeon}
                                /></Suspense>
                                {/* 関連症例：閲覧履歴 */}
                                <Suspense><RelatedCases
                                    sliderId="history"
                                    isOpen={true}
                                    headline={
                                        localized["Label.BrowsingHistory"]
                                    }
                                    operationlist={props.operationlistBrowsingHistory}
                                /></Suspense>
                            </>
                        )}
                    {props.isSponsorUser === true && (
                        // スポンサーユーザー
                        <>
                            {/* 関連症例：関連コンテンツの症例 */}
                            <RelatedCases
                                sliderId="content"
                                isOpen={true}
                                headline={
                                    localized[
                                    "Label.RelatedContent"
                                    ]
                                }
                                operationlist={props.operationlistRelatedContent}
                            />
                        </>
                    )}
                </StyledContentsData>
                {/* コントローラーコンテンツ */}
                {props.isCompletionMediaPlayerFullvideo === true && useMediaQuery(mediaQuery.pcOrPcWide) === true && (
                    <Suspense>
                        <ContentsController
                            setIsOpenModalLogin={props.setIsOpenModalLogin}
                            fnJump={props.fnJump}
                            fnSetModalEdit={props.fnSetModalEdit}
                            isAdminUser={props.isAdminUser}
                            isEditPage={props.isEditPage}
                            isLoadingContentsController={
                                props.isLoadingContentsController
                            }
                            isLoadingTimeline={props.isLoadingTimeline}
                            languageCodeOperation={props.languageCodeOperation}
                            isPaid={props.isPaid}
                            isPurchased={props.isPurchased}
                            listEvent={props.listEvent}
                            listTimeline={props.listTimeline}
                            mainSurgeonId={props.mainSurgeonId}
                            operationId={props.operationId}
                            setClickedSlideIndex={props.setClickedSlideIndex}
                            setIsHighlightPopupPayment={
                                props.setIsHighlightPopupPayment
                            }
                            setIsOpenPopupSchema={props.setIsOpenPopupSchema}
                            setPopupSchemaSlider={props.setPopupSchemaSlider}
                            setSelectedContentsData={props.setSelectedContentsData}
                            timeProgress={props.timeProgress}
                        />
                    </Suspense>
                )}
                {/* ボタンリスト（執刀医権限、管理者権限フル動画ページ、またはスポンサーユーザーの場合は非表示） */}
                {props.isCompletionMediaPlayerFullvideo === true && props.isEditPage !== true &&
                    props.isSponsorUser !== true &&
                    props.isLoadingFullvideo === false &&
                    window.dataUser.isLogin === true && (
                        <Suspense><Buttonlist
                            isIdentificationUser={props.isIdentificationUser}
                            isLimitedReleaseVideo={props.isLimitedReleaseVideo}
                            isPaid={props.isPaid}
                            isPurchased={props.isPurchased}
                            setIsOpenNote={props.setIsOpenNote}
                            setIsOpenPopupPayment={props.setIsOpenPopupPayment}
                            setIsOpenPopupThread={props.setIsOpenPopupThread}
                        /></Suspense>
                    )}
            </StyledContents>
            {/* 機器広告 */}
            {props.isCompletionMediaPlayerFullvideo === true && (
            <Suspense><ContentsAd
                adOperationButtonText={props.adOperationButtonText}
                adOperationId={props.adOperationId}
                isOpenModalAdConfirm={props.isOpenModalAdConfirm}
                isOpenModalAdFailure={props.isOpenModalAdFailure}
                isOpenModalAdSuccess={props.isOpenModalAdSuccess}
                isOpenPopupAd={props.isOpenPopupAd}
                languageCodeOperation={props.languageCodeOperation}
                operationId={props.operationId}
                setIsOpenModalAdConfirm={props.setIsOpenModalAdConfirm}
                setIsOpenModalAdFailure={props.setIsOpenModalAdFailure}
                setIsOpenModalAdSuccess={props.setIsOpenModalAdSuccess}
                setIsOpenPopupAd={props.setIsOpenPopupAd}
            /></Suspense>)}
            {/* シェーマポップアップ */}
            {props.isCompletionMediaPlayerFullvideo === true && (
            <Suspense><PopupSchema
                clickedSlideIndex={props.clickedSlideIndex}
                isOpenPopupSchema={props.isOpenPopupSchema}
                popupSchemaSlider={props.popupSchemaSlider}
                setIsOpenPopupSchema={props.setIsOpenPopupSchema}
            /></Suspense>)}
            {props.isEditPage !== true && (
                // 執刀医権限、管理者権限以外の場合
                <>
                    {props.isCompletionMediaPlayerFullvideo === true && props.isPaid === true && props.isPurchased === false ? (
                        // 有料コンテンツかつ未購入の場合
                        // 決済情報ポップアップ
                        <Suspense><PopupPayment
                            isHighlightPopupPayment={
                                props.isHighlightPopupPayment
                            }
                            isOpenPopupPayment={props.isOpenPopupPayment}
                            productId={props.productId}
                            setIsOpenModalTerms={props.setIsOpenModalTerms}
                            setIsOpenPopupPayment={props.setIsOpenPopupPayment}
                        /></Suspense>
                    ) : (
                        // 有料コンテンツでない、または有料コンテンツで購入済みの場合
                        <>
                            {/* ノート */}
                            {props.isCompletionMediaPlayerFullvideo === true &&
                                props.isLoadingFullvideo === false &&
                                props.timeTotalSecond !== null && (
                                    <>
                                        {fnIsAndroid() === true ||
                                            fnIsIpad() === true ||
                                            fnIsIphone() === true ? (
                                            // Android、iPad、iPhoneの場合、ドロワー型
                                            <Suspense><DrawerNote
                                                fnJump={props.fnJump}
                                                isLoadingFullvideo={
                                                    props.isLoadingFullvideo
                                                }
                                                isOpenDrawerNote={
                                                    props.isOpenNote
                                                }
                                                isPlay={props.isPlay}
                                                operationId={props.operationId}
                                                operationTitle={props.title}
                                                setIsOpenDrawerNote={
                                                    props.setIsOpenNote
                                                }
                                                setSelectedContentsData={
                                                    props.setSelectedContentsData
                                                }
                                                timeProgress={
                                                    props.timeProgress
                                                }
                                            /></Suspense>
                                        ) : (
                                            // 上記以外の場合、ポップアップ型
                                            <Suspense><PopupNote
                                                fnJump={props.fnJump}
                                                isLoadingFullvideo={
                                                    props.isLoadingFullvideo
                                                }
                                                isOpenPopupNote={
                                                    props.isOpenNote
                                                }
                                                isPlay={props.isPlay}
                                                operationId={props.operationId}
                                                operationTitle={props.title}
                                                setIsOpenPopupNote={
                                                    props.setIsOpenNote
                                                }
                                                setSelectedContentsData={
                                                    props.setSelectedContentsData
                                                }
                                                timeProgress={
                                                    props.timeProgress
                                                }
                                            /></Suspense>
                                        )}
                                    </>
                                )}
                            {/* スレッドポップアップ */}
                            {props.isCompletionMediaPlayerFullvideo === true && props.isLimitedReleaseVideo === true && (
                                <Suspense><PopupThread
                                    fnJump={props.fnJump}
                                    isOpenPopupThread={props.isOpenPopupThread}
                                    operationId={props.operationId}
                                    setIsOpenPopupThread={
                                        props.setIsOpenPopupThread
                                    }
                                /></Suspense>
                            )}
                        </>
                    )}
                </>
            )}
            {/* 共通フッター */}
            {props.isCompletionMediaPlayerFullvideo === true && (<Suspense><Footer /></Suspense>)}
            {/* 編集モーダル */}
            {props.isCompletionMediaPlayerFullvideo === true && props.isEditPage === true && (
                // 執刀医権限、管理者権限フル動画ページのみ出力
                <Suspense><ModalEdit
                    content={props.modalEditContent}
                    isOpenModalEdit={props.isOpenModalEdit}
                    mode={props.modalEditMode}
                    operationId={props.operationId}
                    setIsOpenModalEdit={props.setIsOpenModalEdit}
                    setIsResetListComment={props.setIsResetListComment}
                    setIsResetListEvent={props.setIsResetListEvent}
                    setModalEditContent={props.setModalEditContent}
                    setModalEditMode={props.setModalEditMode}
                    setModalEditType={props.setModalEditType}
                    setSurgeonUserList={props.setSurgeonUserList}
                    setTaglist={props.setTaglist}
                    timeProgressSecond={props.timeProgressSecond}
                    type={props.modalEditType}
                /></Suspense>
            )}
            {/* セッションエラーモーダル */}
            {props.isOpenModalErrorSession === true && (
                <Suspense><ModalErrorSession userPlayLogId={props.userPlayLogId} /></Suspense>
            )}
            {/* ローディングエラーモーダル */}
            {props.isOpenModalErrorLoading === true &&
                props.selectedContentsData === "fullvideo" && (
                    <Suspense><ModalErrorLoading
                        setIsShow={props.setIsOpenModalErrorLoading}
                    /></Suspense>
                )}
            {/* 利用規約モーダル */}
            {props.isCompletionMediaPlayerFullvideo === true && props.isOpenModalTerms === true && (
                <Suspense><ModalTermsVideo
                    isOpenModalTerms={props.isOpenModalTerms}
                    setIsOpenModalTerms={props.setIsOpenModalTerms}
                /></Suspense>
            )}
            {/* 利用規約確認モーダル */}
            {props.isCompletionMediaPlayerFullvideo === true && (<Suspense><ModalTerms /></Suspense>)}
            {/* プライバシーポリシーモーダル */}
            {props.isCompletionMediaPlayerFullvideo === true && (<Suspense><ModalPrivacypolicy /></Suspense>)}
        </>
    );
};
